import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Grid,
  Alert,
  alpha,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';

// Animációk
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Styled komponensek
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `
    linear-gradient(135deg, 
      rgba(76, 175, 80, 0.1) 0%, 
      rgba(139, 195, 74, 0.1) 25%,
      rgba(76, 175, 80, 0.05) 50%,
      rgba(139, 195, 74, 0.1) 75%,
      rgba(76, 175, 80, 0.1) 100%
    ),
    radial-gradient(circle at 20% 80%, rgba(76, 175, 80, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(139, 195, 74, 0.2) 0%, transparent 50%)
  `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234caf50' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    pointerEvents: 'none',
  }
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: '24px',
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.95)} 0%, 
    ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: `
    0 20px 40px ${alpha(theme.palette.primary.main, 0.1)},
    0 8px 32px ${alpha(theme.palette.common.black, 0.1)}
  `,
  animation: `${fadeInUp} 0.8s ease-out`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  animation: `${float} 3s ease-in-out infinite`,
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  textAlign: 'center',
}));

const LoginTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: alpha(theme.palette.background.paper, 0.8),
    transition: 'all 0.3s ease',
    '&:hover': {
      background: alpha(theme.palette.background.paper, 1),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      }
    },
    '&.Mui-focused': {
      background: alpha(theme.palette.background.paper, 1),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      }
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    }
  }
}));

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
  transition: 'all 0.3s ease',
  fontWeight: 600,
  fontSize: '1.1rem',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
    transform: 'translateY(-2px)',
  }
}));

const RegisterLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  fontWeight: 500,
  transition: 'all 0.3s ease',
  '&:hover': {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
  }
}));

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const { login, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!email || !password) {
      setFormError('Kérjük, töltse ki az összes mezőt!');
      return;
    }

    const success = await login(email, password);
    if (success) {
      navigate('/gardens');
    }
  };

  return (
    <LoginContainer>
      <Container maxWidth="sm">
        <LoginPaper elevation={0}>
          <LogoContainer>
            <img 
              src="/logo_tb.png" 
              alt="KertMagad logó" 
              style={{ 
                height: '80px', 
                width: 'auto',
                marginRight: '12px'
              }}
            />
          </LogoContainer>
          
          <BrandTitle variant="h4">
            KertMagad
          </BrandTitle>
          
          <LoginTitle variant="h5">
            Bejelentkezés
          </LoginTitle>

          {(formError || error) && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: '12px',
                background: alpha('#f44336', 0.1),
                border: `1px solid ${alpha('#f44336', 0.2)}`
              }}
            >
              {formError || error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} data-testid="login-form">
            <StyledTextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email cím"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="email-input"
              InputProps={{
                startAdornment: (
                  <PersonIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
              }}
            />
            <StyledTextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Jelszó"
              data-testid="password-input"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <LockIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
              }}
            />
            <LoginButton
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 4, mb: 3 }}
              startIcon={<LocalFloristIcon />}
              data-testid="login-submit-button"
            >
              Bejelentkezés
            </LoginButton>
            <Grid container justifyContent="center">
              <Grid>
                <RegisterLink to="/register">
                  <Typography variant="body1" align="center">
                    Nincs még fiókja? Regisztráljon!
                  </Typography>
                </RegisterLink>
              </Grid>
            </Grid>
          </Box>
        </LoginPaper>
      </Container>
    </LoginContainer>
  );
};

export default Login; 